import React from 'react';
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  differenceInMonths,
  endOfMonth,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parse,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { DateRange, DayPicker, Matcher } from 'react-day-picker';
import { fi } from 'react-day-picker/locale';
import { useTranslation } from 'react-i18next';

import styles from './styles/DatePicker.module.scss';

// Utils.
import { capitalizeFirstLetter } from '../../utils/consumption';
import { ContractBasicData } from '../../types/contract';
import { resetController } from '../../utils/abortController';

export { controller } from '../../utils/abortController';

type DatePickerProps = {
  type: 'single' | 'range';
  day?: {
    date: Date | undefined;
    setDate: (value: React.SetStateAction<Date | undefined>) => void;
  };
  dates?: {
    startDate: Date | undefined;
    endDate: Date | undefined;
    setStartDate: ((value: React.SetStateAction<Date | undefined>) => void) | ((value: Date | undefined) => void);
    setEndDate: ((value: React.SetStateAction<Date | undefined>) => void) | ((value: Date | undefined) => void);
  };
  limits?: {
    minDate: Date | undefined;
    maxDate: Date | undefined;
    maxLength?: number;
  };
  prevNext?: boolean;
  resolution?: {
    resolution: string;
    setResolution: ((value: React.SetStateAction<string>) => void) | ((value: string) => void);
  };
  onDateChange?: (date: Date | undefined) => void;
  presets?: React.JSX.Element;
  contract?: ContractBasicData;
  customFormat?: string;
  graphDateStyle?: string;
  consumptionResolution?: boolean;
};

const DatePicker: React.FunctionComponent<DatePickerProps> = (props: DatePickerProps) => {
  const { t } = useTranslation();

  const { type, day, dates, limits, prevNext, resolution, onDateChange, presets, contract, customFormat, graphDateStyle, consumptionResolution } =
    props;

  const [month, setMonth] = React.useState<Date | undefined>(day?.date || dates?.startDate || undefined);

  const [inputValue, setInputValue] = React.useState<string>(day?.date?.toLocaleDateString('fi') || '');
  const [startInputValue, setStartInputValue] = React.useState<string>(dates?.startDate?.toLocaleDateString('fi') || '');
  const [endInputValue, setEndInputValue] = React.useState<string>(dates?.endDate?.toLocaleDateString('fi') || '');
  const [selectedInput, setSelectedInput] = React.useState<'day' | 'start' | 'end' | undefined>(undefined);

  const [showDayPicker, setShowDayPicker] = React.useState<boolean>(false);

  React.useEffect(() => {
    setMonth(day?.date || dates?.startDate || undefined);
    setInputValue(day?.date?.toLocaleDateString('fi') || '');
    setStartInputValue(dates?.startDate?.toLocaleDateString('fi') || '');
    setEndInputValue(dates?.endDate?.toLocaleDateString('fi') || '');
  }, [day?.date, dates?.startDate, dates?.endDate]);

  const handleDayPickerSelect = (date: Date | undefined) => {
    if (!date) {
      setInputValue('');
      day?.setDate(undefined);
      if (onDateChange) {
        onDateChange(undefined);
      }
    } else {
      day?.setDate(date);
      if (onDateChange) {
        onDateChange(date);
      }
      setMonth(date);
      setInputValue(date.toLocaleDateString('fi'));
    }
    setSelectedInput(undefined);
    setShowDayPicker(false);
  };

  const handleDateRangePickerSelect = (dateRange: DateRange | undefined, selectedDay: Date | undefined) => {
    if (!dateRange) {
      setStartInputValue('');
      setEndInputValue('');
      dates?.setStartDate(undefined);
      dates?.setEndDate(undefined);
    } else {
      let newStart = dates?.startDate;
      let newEnd = dates?.endDate;

      if (selectedInput === 'start') {
        if (selectedDay && newEnd && isAfter(selectedDay, newEnd)) {
          newEnd = selectedDay;
          newStart = dates?.endDate;
        } else {
          newStart = selectedDay;
        }
      }
      if (selectedInput === 'end') {
        if (selectedDay && newStart && isBefore(selectedDay, newStart)) {
          newStart = selectedDay;
          newEnd = dates?.startDate;
        } else {
          newEnd = selectedDay;
        }
      }

      handleDatesChange({
        startDate: newStart,
        endDate: newEnd,
      });

      if (selectedInput === 'start') {
        setSelectedInput('end');
      }
      if (selectedInput === 'end') {
        setShowDayPicker(false);
        setSelectedInput(undefined);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const type = e.target.dataset.datepickerfield;

    if (type === 'start') {
      setStartInputValue(e.target.value);
    } else if (type === 'end') {
      setEndInputValue(e.target.value);
    } else {
      setInputValue(e.target.value);
    }

    const parsedDate = parse(e.target.value, 'dd.MM.yyyy', new Date());

    if (isValid(parsedDate)) {
      setMonth(parsedDate);

      if (type === 'start') {
        dates?.setStartDate(parsedDate);
      } else if (type === 'end') {
        dates?.setEndDate(parsedDate);
      } else {
        day?.setDate(parsedDate);
        if (onDateChange) {
          onDateChange(parsedDate);
        }
      }
    } else {
      if (type === 'start') {
        dates?.setStartDate(undefined);
      } else if (type === 'end') {
        dates?.setEndDate(undefined);
      } else {
        day?.setDate(undefined);
        if (onDateChange) {
          onDateChange(undefined);
        }
      }
    }
  };

  const handleTimeSelectionPrevNext = ({
    startDate,
    endDate,
    isNext,
  }: {
    startDate: Date | undefined;
    endDate: Date | undefined;
    isNext: boolean;
  }): void => {
    let newStart: Date | undefined = undefined;
    let newEnd: Date | undefined = undefined;

    if (startDate && endDate) {
      const isStartOfYear = isSameDay(startDate, startOfYear(startDate));
      const isEndOfYear = isSameDay(endDate, endOfYear(endDate));
      const isStartOfMonth = isSameDay(startDate, startOfMonth(startDate));
      const isEndOfMonth = isSameDay(endDate, endOfMonth(endDate));

      const diffDays = differenceInCalendarDays(endDate, startDate) + 1;
      const diffMonths = differenceInMonths(endDate, startDate) + 1;

      if (isStartOfYear && isEndOfYear) {
        if (!isNext) {
          newStart = startOfYear(subYears(startDate, 1));
          newEnd = endOfYear(subYears(endDate, 1));
        } else {
          newStart = startOfYear(addYears(startDate, 1));
          newEnd = endOfYear(addYears(endDate, 1));
        }
        if (resolution) {
          resolution.setResolution(consumptionResolution ? 'year' : 'month');
        }
      } else if (isStartOfMonth && isEndOfMonth) {
        if (diffDays > 31) {
          if (!isNext) {
            newStart = subMonths(startDate, diffMonths);
            newEnd = subMonths(endDate, diffMonths);
          } else {
            newStart = addMonths(startDate, diffMonths);
            newEnd = addMonths(endDate, diffMonths);
          }
        } else {
          if (!isNext) {
            newStart = startOfMonth(subMonths(startDate, 1));
            newEnd = endOfMonth(subMonths(endDate, 1));
          } else {
            newStart = startOfMonth(addMonths(startDate, 1));
            newEnd = endOfMonth(addMonths(endDate, 1));
          }
        }
      } else {
        if (!isNext) {
          newStart = subDays(startDate, diffDays);
          newEnd = subDays(endDate, diffDays);
        } else {
          newStart = addDays(startDate, diffDays);
          newEnd = addDays(endDate, diffDays);
        }
      }
    }

    resetController();
    handleDatesChange({ startDate: newStart, endDate: newEnd });
  };

  const handleDatesChange = ({ startDate, endDate }: { startDate: Date | undefined; endDate: Date | undefined }): void => {
    let start = startDate;
    let end = endDate;

    if (contract) {
      const contractStart = new Date(contract.contractStartDate);
      const contractEnd = new Date(contract.contractEndDate);
      // limit start to contractStart
      start = startDate && startDate <= contractStart ? contractStart : startDate;
      // limit end to contractEnd but only if start is before contractEnd
      end = contract?.contractEndDate && endDate && endDate > contractEnd && start && start < contractEnd ? contractEnd : endDate;
    }

    setMonth(start);
    setStartInputValue(start?.toLocaleDateString('fi') || '');
    setEndInputValue(end?.toLocaleDateString('fi') || '');
    dates?.setStartDate(start);
    dates?.setEndDate(end);

    if (start && end && resolution && isSameDay(start, end)) {
      resolution.setResolution(consumptionResolution ? 'day' : 'hour');
    }
  };

  React.useEffect(() => {
    if (showDayPicker) {
      const element = datePickerRef.current;

      element.style.right = null;

      const elementRect = element.getBoundingClientRect();

      if (elementRect.left > window.innerWidth - elementRect.width) {
        element.style.right = 0;
      }
    }
  }, [showDayPicker]);

  const escFunction = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showDayPicker) {
          datePickerToggleRef.current.focus();
        }
        setShowDayPicker(false);
        setSelectedInput(undefined);
      }
    },
    [showDayPicker, setShowDayPicker],
  );

  React.useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  function useOutsideAlerter(
    ref: React.RefObject<any>,
    open: {
      showDayPicker: boolean;
      setShowDayPicker: (value: React.SetStateAction<boolean>) => void;
    },
  ) {
    React.useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target)) {
          open.setShowDayPicker(false);
          setSelectedInput(undefined);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, open]);
  }

  const datePickerWrapperRef = React.useRef<any>(null);
  const datePickerToggleRef = React.useRef<any>(null);
  const datePickerRef = React.useRef<any>(null);
  useOutsideAlerter(datePickerWrapperRef, { showDayPicker, setShowDayPicker });

  const pickerId = React.useId();

  const matcher: Matcher = (day: Date) => {
    let returnValue = false;

    if (limits?.minDate && !returnValue) {
      returnValue = isBefore(day, limits.minDate) && !isSameDay(day, limits.minDate);
    }
    if (limits?.maxDate && !returnValue) {
      returnValue = isAfter(day, limits.maxDate) && !isSameDay(day, limits.maxDate);
    }
    if (limits?.maxLength && dates?.startDate && !returnValue) {
      returnValue = differenceInCalendarDays(dates.startDate, day) >= limits.maxLength;
    }
    if (limits?.maxLength && dates?.endDate && !returnValue) {
      returnValue = differenceInCalendarDays(dates.endDate, day) >= limits.maxLength;
    }
    return returnValue;
  };

  return (
    <div className={styles.datePickerWrapper} ref={datePickerWrapperRef}>
      <div className={`${styles.datePicker} ${styles[graphDateStyle || '']}`}>
        {type === 'single' && (
          <>
            {prevNext && (
              <>
                <button
                  className={styles.prevButton}
                  type="button"
                  aria-label={t('common:general.previous')}
                  onClick={(): void => {
                    if (day?.date) {
                      const newDay = subDays(day.date, 1);
                      day.setDate(newDay);
                      if (onDateChange) {
                        onDateChange(newDay);
                      }
                      setMonth(newDay);
                      setInputValue(newDay.toLocaleDateString('fi'));
                    }
                  }}
                />
                <button
                  className={styles.nextButton}
                  type="button"
                  aria-label={t('common:general.next')}
                  aria-disabled={day?.date && limits?.maxDate && (isSameDay(day?.date, limits?.maxDate) || isAfter(day?.date, limits?.maxDate))}
                  onClick={(e): void => {
                    // @ts-ignore
                    if (e.target.ariaDisabled === 'false' && day?.date) {
                      const newDay = addDays(day.date, 1);
                      day.setDate(newDay);
                      if (onDateChange) {
                        onDateChange(newDay);
                      }
                      setMonth(newDay);
                      setInputValue(newDay.toLocaleDateString('fi'));
                    }
                  }}
                />
              </>
            )}
            <div className={styles.datePickerField}>
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onMouseDown={() => {
                  setShowDayPicker(true);
                  setSelectedInput('day');
                }}
                data-datepickerfield="day"
                className={selectedInput === 'day' ? styles.active : undefined}
              />
              <button
                ref={datePickerToggleRef}
                type="button"
                aria-controls={pickerId}
                onClick={() => {
                  setShowDayPicker(!showDayPicker);
                  setSelectedInput(selectedInput === 'day' ? undefined : 'day');

                  setTimeout(() => {
                    datePickerRef.current.querySelector('.rdp-selected button').focus();
                  }, 0);
                }}
              />
            </div>
          </>
        )}
        {type === 'range' && (
          <>
            {prevNext && (
              <>
                <button
                  className={styles.prevButton}
                  type="button"
                  aria-label={t('common:general.previous')}
                  aria-disabled={
                    dates?.startDate &&
                    limits?.minDate &&
                    (isSameDay(dates?.startDate, limits?.minDate) || isBefore(dates?.startDate, limits?.minDate))
                  }
                  onClick={(e): void => {
                    // @ts-ignore
                    if (e.target.ariaDisabled === 'false') {
                      handleTimeSelectionPrevNext({
                        startDate: dates?.startDate,
                        endDate: dates?.endDate,
                        isNext: false,
                      });
                    }
                  }}
                />
                <button
                  className={styles.nextButton}
                  type="button"
                  aria-label={t('common:general.next')}
                  aria-disabled={
                    dates?.endDate && limits?.maxDate && (isSameDay(dates?.endDate, limits?.maxDate) || isAfter(dates?.endDate, limits?.maxDate))
                  }
                  onClick={(e): void => {
                    // @ts-ignore
                    if (e.target.ariaDisabled === 'false') {
                      handleTimeSelectionPrevNext({
                        startDate: dates?.startDate,
                        endDate: dates?.endDate,
                        isNext: true,
                      });
                    }
                  }}
                />
              </>
            )}
            <div className={styles.datePickerField}>
              <input
                type="text"
                value={customFormat && dates?.startDate ? format(dates.startDate, customFormat, { locale: fi }) : startInputValue}
                onChange={handleInputChange}
                onMouseDown={() => {
                  setShowDayPicker(true);
                  setSelectedInput('start');
                }}
                data-datepickerfield="start"
                className={selectedInput === 'start' ? styles.active : undefined}
              />
              {!(
                customFormat &&
                dates?.startDate &&
                dates.endDate &&
                format(dates.startDate, customFormat, { locale: fi }) === format(dates.endDate, customFormat, { locale: fi })
              ) && (
                <>
                  <span>&ndash;</span>
                  <input
                    type="text"
                    value={customFormat && dates?.endDate ? format(dates.endDate, customFormat, { locale: fi }) : endInputValue}
                    onChange={handleInputChange}
                    onMouseDown={() => {
                      setShowDayPicker(true);
                      setSelectedInput('end');
                    }}
                    data-datepickerfield="end"
                    className={selectedInput === 'end' ? styles.active : undefined}
                  />
                </>
              )}
              <button
                ref={datePickerToggleRef}
                type="button"
                aria-controls={pickerId}
                onClick={() => {
                  setShowDayPicker(!showDayPicker);

                  setSelectedInput('start');

                  setTimeout(() => {
                    datePickerRef.current.querySelector('.rdp-selected button').focus();
                  }, 0);
                }}
              />
            </div>
          </>
        )}
      </div>
      <div id={pickerId} ref={datePickerRef} className={styles.dayPickerModal} aria-expanded={showDayPicker}>
        <div className={styles.dayPickerHeader}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              setShowDayPicker(false);
              setSelectedInput(undefined);
            }}
          />
          <h4 aria-live="polite">
            {t(
              `consumption:consumption.${type === 'range' ? 'range' : 'day'}Select${
                type === 'range' ? capitalizeFirstLetter(selectedInput || '') : ''
              }`,
            )}
          </h4>
        </div>
        {type === 'single' && (
          <DayPicker
            locale={fi}
            animate
            required
            mode="single"
            month={month}
            onMonthChange={setMonth}
            startMonth={limits?.minDate}
            endMonth={limits?.maxDate}
            disabled={matcher}
            selected={day?.date}
            onSelect={handleDayPickerSelect}
          />
        )}
        {type === 'range' && (
          <DayPicker
            locale={fi}
            animate
            required
            showOutsideDays
            showWeekNumber
            mode="range"
            numberOfMonths={2}
            month={month}
            onMonthChange={setMonth}
            startMonth={limits?.minDate}
            endMonth={limits && limits.maxDate ? addMonths(limits.maxDate, 1) : undefined}
            disabled={matcher}
            selected={{ from: dates?.startDate, to: dates?.endDate }}
            onSelect={handleDateRangePickerSelect}
          />
        )}
        {presets && (
          <div className={styles.presets}>
            <h4>{t('consumption:consumption.presets')}</h4>
            {presets}
          </div>
        )}
      </div>
    </div>
  );
};

export default DatePicker;
